/**
 * The brick stack: full-width layers from the bottom of the well, styled by
 * the theme's brick style (bricks.ts picks the shapes, game.css the
 * textures). The top layer can be breaking (cracks, then it bursts) or
 * forming (a wrong answer stretching into place with a gentle bump).
 */
import { memo, useMemo, type CSSProperties } from 'react';
import type { BrickStyle } from '@dropstack/core';
import { brickLayerLook } from './bricks.ts';

/** Custom properties for inline styles (React's CSSProperties has no index signature for them). */
export function cssVars(vars: Record<`--${string}`, string | number>): CSSProperties {
  return vars as CSSProperties;
}

/** Overshoot-and-settle, for the forming layer's stretch. */
const easeOutBack = (t: number) => {
  const c = 1.9;
  return 1 + (c + 1) * (t - 1) ** 3 + c * (t - 1) ** 2;
};

interface BrickLayerProps {
  brickStyle: BrickStyle;
  index: number;
  /** Cracking progress 0 → 1 (top layer about to burst). */
  breaking?: number | null;
  /** Stretch-in progress 0 → 1 (a new layer forming). */
  forming?: number | null;
  reducedMotion: boolean;
}

/** Hairline cracks drawn over a breaking layer. */
function Cracks() {
  return (
    <svg className="brick-layer__cracks" viewBox="0 0 100 20" preserveAspectRatio="none" aria-hidden="true">
      <polyline points="18,0 22,7 17,12 24,20" />
      <polyline points="47,0 44,6 50,11 46,20" />
      <polyline points="74,0 79,8 73,13 78,20" />
      <polyline points="22,7 33,9 44,6" />
      <polyline points="50,11 62,9 73,13" />
    </svg>
  );
}

const BrickLayer = memo(function BrickLayer({ brickStyle, index, breaking = null, forming = null, reducedMotion }: BrickLayerProps) {
  const look = useMemo(() => brickLayerLook(brickStyle, index), [brickStyle, index]);
  const vars: Record<`--${string}`, string | number> = { '--i': index };
  let transform: string | undefined;
  if (forming !== null && !reducedMotion) {
    const t = easeOutBack(forming);
    const bump = Math.sin(forming * Math.PI) * 0.18;
    transform = `scale(${0.5 + 0.5 * t}, ${1 + bump})`;
  }
  const crackOpacity = breaking === null ? 0 : Math.min(1, Math.max(0, (breaking - 0.55) / 0.3));
  const classes = [look.className, breaking !== null && 'brick-layer--breaking', forming !== null && 'brick-layer--forming']
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes} style={{ ...cssVars(vars), transform }}>
      {look.bricks.map((b, i) => (
        <span key={i} className="brick" style={{ flexGrow: b.grow, ...cssVars({ '--tone': b.tone, '--grain': b.grain }) }} />
      ))}
      {crackOpacity > 0 && (
        <span className="brick-layer__crack-wrap" style={{ opacity: crackOpacity }}>
          <Cracks />
        </span>
      )}
    </div>
  );
});

export interface BrickStackProps {
  brickStyle: BrickStyle;
  layers: number;
  breakingTop: number | null;
  formingTop: number | null;
  reducedMotion: boolean;
}

export function BrickStack({ brickStyle, layers, breakingTop, formingTop, reducedMotion }: BrickStackProps) {
  return (
    <div className="well__stack" aria-hidden="true">
      {Array.from({ length: layers }, (_, i) => {
        const top = i === layers - 1;
        return (
          <BrickLayer
            key={i}
            brickStyle={brickStyle}
            index={i}
            breaking={top ? breakingTop : null}
            forming={top ? formingTop : null}
            reducedMotion={reducedMotion}
          />
        );
      })}
    </div>
  );
}
